import { Link } from 'react-router-dom';
import { Callout, CopyButton, StatusChip } from '@nyce/admin-kit/console';
import { Button, TextField } from '@nyce/admin-kit/ui';
import {
  Card,
  EmptyState,
  ErrorBox,
  Loading,
  SortSelect,
  SortTh,
  TableScroller,
  useAsync,
  useSort,
} from '../../components/common';
import { formatCount } from '../../lib/format';
import { candidatePath, indexAdvisor, type Proposal, type Scan } from '../api/indexAdvisor';
import { saveSql, useParam } from '../shared';

/** The scan's proposed default-set changes as `index-additions.sql` blocks (for a toolkit PR), never applied. */
export function RecommendationTab({ scan, stateKey }: { scan: Scan; stateKey: string }) {
  const [atLeast, setAtLeast] = useParam('atLeast', '');
  const minDatabases = Number(atLeast) > 0 ? Number(atLeast) : undefined;
  const rec = useAsync(
    () => indexAdvisor.recommendation(scan.id, minDatabases),
    [scan.id, minDatabases, stateKey]
  );
  const sort = useSort<Proposal>(
    rec.data?.proposals ?? [],
    {
      databases: { label: 'Databases', value: (p) => p.databases, desc: true },
      table: { label: 'Table', value: (p) => p.table },
      class: { label: 'Class', value: (p) => p.class },
    },
    useParam('sort', '')
  );
  const r = rec.data;
  const file = `index-additions-scan-${scan.id}.sql`;
  return (
    <>
      <div className="iax-toolbar">
        <TextField
          aria-label="Min databases"
          placeholder="Min databases (2)"
          type="number"
          min={1}
          inputMode="numeric"
          fieldClassName="iax-num-field"
          value={atLeast}
          onChange={(e) => setAtLeast(e.target.value)}
        />
        <SortSelect sort={sort} />
      </div>
      <ErrorBox error={rec.error} />
      <Loading loading={rec.loading && !r} />
      {r && !r.reviewed && (
        <Callout tone="info" title="Not reviewed">
          Without a review the proposals come from the advisor's counts and the default-set check alone: every
          new index recommended by enough databases, as <code>general</code>. Review the scan for verdicts,
          widened indexes and reasons.
        </Callout>
      )}
      {r && !r.proposals.length && (
        <EmptyState card title="Nothing to add." hint="No candidate passes the threshold and the review." />
      )}
      {!!sort.rows.length && (
        <div className="nyk-card">
          <TableScroller>
            <table className="nyk-ui-table nyk-cards" aria-label="Proposals" data-testid="iax-proposals">
              <thead>
                <tr>
                  <th>Index</th>
                  <SortTh sort={sort} by="table" />
                  <SortTh sort={sort} by="databases" className="nyk-ui-num" />
                  <SortTh sort={sort} by="class" />
                  <th>Why</th>
                </tr>
              </thead>
              <tbody>
                {sort.rows.map((p) => (
                  <tr key={p.candidateId} data-proposal={p.candidateId}>
                    <td className="nyk-card-title">
                      <Link to={candidatePath(scan.id, p.candidateId)} className="iax-ddl">
                        {p.ddl}
                      </Link>
                      <div className="nyk-ui-hint">{p.source}</div>
                    </td>
                    <td data-label="Table" className="nyk-wrap">
                      {p.table}
                    </td>
                    <td data-label="Databases" className="nyk-ui-num">
                      {formatCount(p.databases)}
                    </td>
                    <td data-label="Class" className="nyk-wrap">
                      <StatusChip
                        value={p.class}
                        tone={p.class === 'general' ? 'green' : 'blue'}
                        label={p.class}
                      />
                      {p.when && <div className="nyk-ui-hint">when {p.when}</div>}
                    </td>
                    <td data-label="Why" className="nyk-wrap">
                      {p.why ?? '—'}
                      {p.cost && <div className="nyk-ui-hint">cost: {p.cost}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroller>
        </div>
      )}
      {r && !!r.sql && (
        <Card
          title="index-additions.sql"
          hint="Blocks in the toolkit's format: paste them into a PR on the go-live checklist's index-additions.sql."
          actions={
            <>
              <CopyButton text={r.sql} label="Copy" />
              <Button size="sm" variant="secondary" onClick={() => saveSql(r.sql, file)}>
                Download
              </Button>
            </>
          }
        >
          <pre className="iax-sql-block" data-testid="iax-additions-sql">
            {r.sql}
          </pre>
        </Card>
      )}
    </>
  );
}
